<template>
  <v-container fluid class="kg-page">
    <v-card class="card2 kg-card" style="margin-top: 1rem">
      <v-card-text>
        <div class="kg-heading">
          <h2 class="kg-title">{{ $t('graphRagGraphList') }}</h2>
          <div class="kg-subtitle">{{ $t('graphRagGraphListHint') }}</div>
        </div>

        <div class="kg-toolbar">
          <div class="kg-search">
            <v-icon size="20" color="#9E9E9E">mdi-magnify</v-icon>
            <input
              v-model="search"
              data-testid="graph-search"
              type="text"
              :placeholder="$t('graphRagSearchGraph')"
              @input="onSearchInput"
            />
          </div>

          <!-- โหลดรายการใหม่จากเซิร์ฟเวอร์ ใช้ตอนมีคนอื่นแก้กราฟพร้อมกัน -->
          <button type="button" class="kg-btn-outline" data-testid="graph-sync" :disabled="loading" @click="syncGraphs">
            <v-icon size="18">mdi-refresh</v-icon>
            <span>{{ $t('graphRagSync') }}</span>
          </button>

          <button type="button" class="kg-btn-solid" data-testid="graph-add" @click="openAdd">
            <v-icon size="18" color="#ffffff">mdi-plus</v-icon>
            <span>{{ $t('graphRagCreateGraph') }}</span>
          </button>
        </div>
      </v-card-text>

      <v-data-table-server
        v-model:options="options"
        :items-per-page="options.itemsPerPage"
        :items-length="total"
        :items="rows"
        :headers="headers"
        :loading="loading"
        class="kg-table px-4 pb-4"
        data-testid="graph-table"
        @update:options="loadGraphs"
      >
        <template #no-data>
          <div style="text-align: center; padding: 24px 0">
            <span style="color: #646464; font-weight: 400">{{ $t('noDataAvailable') }}</span>
          </div>
        </template>

        <template #[`item.name`]="{ item }">
          <div class="kg-name" :data-testid="`graph-open-${item.graphKey}`" @click="openEditor(item)">{{ item.name }}</div>
          <div class="kg-key">{{ item.id }}</div>
        </template>

        <template #[`item.description`]="{ item }">
          <span class="kg-desc">{{ item.description || '' }}</span>
        </template>

        <template #[`item.nodeCount`]="{ item }">
          <span class="kg-chip kg-chip-node" :data-testid="`graph-nodes-${item.graphKey}`">● {{ item.nodeCount }}</span>
        </template>

        <template #[`item.edgeCount`]="{ item }">
          <span class="kg-chip kg-chip-edge" :data-testid="`graph-edges-${item.graphKey}`">↗ {{ item.edgeCount }}</span>
        </template>

        <template #[`item.currentVersionNo`]="{ item }">
          <span :data-testid="`graph-version-${item.graphKey}`">{{ item.currentVersionNo ? 'v' + item.currentVersionNo : '—' }}</span>
        </template>

        <template #[`item.updateDate`]="{ item }">
          <span :data-testid="`graph-updated-${item.graphKey}`">{{ formatDate(item.updateDate) }}</span>
        </template>

        <template #[`item.manage`]="{ item }">
          <div class="kg-actions">
            <button type="button" class="kg-icon-btn" :title="$t('graphRagOpenGraph')" :data-testid="`graph-view-${item.graphKey}`" @click="openEditor(item)">
              <v-icon size="19">mdi-eye-outline</v-icon>
            </button>
            <button
              type="button"
              class="kg-icon-btn"
              :title="$t('edit')"
              :disabled="!canEdit(item)"
              :data-testid="`graph-edit-${item.graphKey}`"
              @click="canEdit(item) && openEdit(item)"
            >
              <v-icon size="19">mdi-pencil-outline</v-icon>
            </button>
            <button
              type="button"
              class="kg-icon-btn"
              :title="$t('delete')"
              :disabled="!canManage(item)"
              :data-testid="`graph-delete-${item.graphKey}`"
              @click="canManage(item) && confirmDelete(item)"
            >
              <v-icon size="19">mdi-delete-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- เพิ่ม / แก้ไข -->
    <v-dialog v-model="showForm" max-width="600" persistent>
      <v-card>
        <v-card-title class="d-flex align-center pt-4">
          <v-icon start color="#C32E0E">{{ isEdit ? 'mdi-pencil' : 'mdi-plus-circle-outline' }}</v-icon>
          {{ isEdit ? $t('graphRagEditGraph') : $t('graphRagAddGraph') }}
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" data-testid="graph-form-name" :label="$t('graphRagName')" :error-messages="formErrors.name" variant="outlined" density="comfortable" class="mb-2" />
          <v-textarea v-model="form.description" data-testid="graph-form-description" :label="$t('description')" variant="outlined" density="comfortable" rows="3" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="graph-form-cancel" @click="showForm = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="graph-form-save" :loading="saving" @click="saveGraph">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ลบ -->
    <v-dialog v-model="showDelete" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ $t('delete') }}</v-card-title>
        <v-card-text>{{ $t('graphRagDeleteGraphConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="graph-delete-cancel" @click="showDelete = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" data-testid="graph-delete-confirm" :loading="saving" @click="deleteGraph">{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="toast.show" :color="toast.colour" timeout="4000">{{ toast.text }}</v-snackbar>
  </v-container>
</template>

<script>
// backend เก็บ UpdateDate ด้วย DateTime.UtcNow แต่ค่าที่อ่านกลับจาก SQL Server ไม่มี offset (เช่น "2026-10-01T07:00:21.96")
// ⇒ ต้องถือเป็น UTC เอง ไม่งั้น browser ตีเป็นเวลาเครื่องแล้วเวลาช้าไป 7 ชม.
const CONST_ISO_HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
// ล็อกเป็นเวลาไทยเสมอ ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้
const thaiDateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export default {
  name: 'GraphManager',
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      search: '',
      graphType: '',
      searchTimer: null,
      options: { page: 1, itemsPerPage: 10, sortBy: [] },
      showForm: false,
      showDelete: false,
      isEdit: false,
      form: { id: 0, name: '', graphKey: '', description: '', graphType: 'knowledge' },
      formErrors: { name: [] },
      pendingDelete: null,
      toast: { show: false, text: '', colour: 'success' },
      // ต้องตรงกับ ValidGraphTypes ใน BAL/GraphRAG/Storage/GraphRagGraphBal.cs เป๊ะ
      // ไม่งั้นผู้ใช้เลือกจาก dropdown แล้วบันทึกไม่ผ่าน โดยไม่มีอะไรบอกว่าเลือกผิดตรงไหน
      graphTypeChoices: ['knowledge', 'database', 'hierarchy', 'mixed'],
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('graphRagName'), value: 'name', align: 'start', sortable: false, width: '30%' },
        { title: this.$t('description'), value: 'description', align: 'start', sortable: false, width: '16%' },
        { title: this.$t('graphRagNodeCountCol'), value: 'nodeCount', align: 'start', sortable: false, width: '10%' },
        { title: this.$t('graphRagEdgeCountCol'), value: 'edgeCount', align: 'start', sortable: false, width: '10%' },
        { title: this.$t('graphRagLatestVersion'), value: 'currentVersionNo', align: 'start', sortable: false, width: '10%' },
        { title: this.$t('graphRagUpdatedAt'), value: 'updateDate', align: 'start', sortable: false, width: '14%' },
        { title: this.$t('actions'), value: 'manage', align: 'center', sortable: false, width: '10%' },
      ];
    },
  },
  mounted() {
    this.loadGraphs();
  },
  methods: {
    // ระดับสิทธิ์มาพร้อมแต่ละแถวแล้ว จึงไม่ต้องยิงถามซ้ำต่อปุ่ม
    canEdit(item) {
      return item.accessLevel === 'edit' || item.accessLevel === 'manage';
    },
    canManage(item) {
      return item.accessLevel === 'manage';
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.options.page = 1;
        this.loadGraphs();
      }, 400);
    },
    loadGraphs() {
      this.loading = true;

      const query = new URLSearchParams({
        search: this.search || '',
        graphType: this.graphType || '',
        page: this.options.page,
        pageSize: this.options.itemsPerPage,
      }).toString();

      this.$call.GraphRagGetGraphs(
        query,
        (res) => {
          this.loading = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.rows = res.data?.data || [];
          this.total = res.data?.total || 0;
        },
        () => {
          this.loading = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    /**
     * วันเวลาไทย (Asia/Bangkok) — ปี พ.ศ. ตามที่หน้าอื่นของระบบใช้
     * ค่าจาก backend ที่ไม่มี offset = UTC · ค่าว่างคืนขีด ไม่ใช่ Invalid Date
     */
    formatDate(value) {
      if (!value) return '—';

      const text = String(value);
      const d = new Date(text.includes('T') && !CONST_ISO_HAS_OFFSET.test(text) ? `${text}Z` : text);
      if (Number.isNaN(d.getTime())) return '—';

      const parts = Object.fromEntries(thaiDateTimeFormat.formatToParts(d).map((p) => [p.type, p.value]));
      return `${parts.day}/${parts.month}/${Number(parts.year) + 543} ${parts.hour}:${parts.minute}`;
    },
    /** โหลดรายการใหม่จากเซิร์ฟเวอร์ — ใช้ตอนมีคนอื่นแก้กราฟพร้อมกัน */
    syncGraphs() {
      this.options.page = 1;
      this.loadGraphs();
    },
    openEditor(item) {
      this.$router.push({ name: 'KnowledgeGraphDetail', params: { graphKey: item.graphKey } });
    },
    openAdd() {
      this.isEdit = false;
      this.form = { id: 0, name: '', graphKey: '', description: '', graphType: 'knowledge' };
      this.formErrors = { name: [] };
      this.showForm = true;
    },
    openEdit(item) {
      this.isEdit = true;
      this.form = {
        id: item.id,
        name: item.name,
        graphKey: item.graphKey,
        description: item.description,
        graphType: item.graphType || 'knowledge',
      };
      this.formErrors = { name: [] };
      this.showForm = true;
    },
    validateForm() {
      this.formErrors = { name: [] };

      if (!this.form.name || this.form.name.trim().length === 0) {
        this.formErrors.name = [this.$t('graphRagNameRequired')];
      }

      return this.formErrors.name.length === 0;
    },
    saveGraph() {
      if (!this.validateForm()) return;

      this.saving = true;

      const done = (res) => {
        this.saving = false;
        if (res.status !== 0) return this.notify(res.message, 'error');

        this.showForm = false;
        this.notify(this.$t('graphRagSaved'), 'success');
        this.loadGraphs();
      };
      // สร้างเสร็จให้เข้าหน้ากราฟทันที ผู้ใช้จะได้ทำงานต่อโดยไม่ต้องกลับมาหาในตาราง
      const doneCreate = (res) => {
        this.saving = false;
        if (res.status !== 0) return this.notify(res.message, 'error');

        this.showForm = false;
        if (res.data?.graphKey) {
          this.openEditor(res.data);
          return;
        }

        this.notify(this.$t('graphRagSaved'), 'success');
        this.loadGraphs();
      };
      const fail = () => {
        this.saving = false;
        this.notify(this.$t('graphRagRequestFailed'), 'error');
      };

      if (this.isEdit) {
        this.$call.GraphRagUpdateGraph(
          { id: this.form.id, name: this.form.name, description: this.form.description, graphType: this.form.graphType },
          done,
          fail
        );
      } else {
        this.$call.GraphRagAddGraph(this.form, doneCreate, fail);
      }
    },
    confirmDelete(item) {
      this.pendingDelete = item;
      this.showDelete = true;
    },
    deleteGraph() {
      this.saving = true;
      this.$call.GraphRagDeleteGraph(
        { id: this.pendingDelete.id },
        (res) => {
          this.saving = false;
          this.showDelete = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagDeleted'), 'success');
          this.loadGraphs();
        },
        () => {
          this.saving = false;
          this.showDelete = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    notify(text, colour) {
      this.toast = { show: true, text, colour: colour || 'success' };
    },
  },
};
</script>

<style scoped>
/* ── หน้ารายการกราฟ — ตามดีไซน์ที่ทีมใช้อยู่ ────────────────── */
.kg-heading { margin-bottom: 18px; }
.kg-title { font-weight: 700; font-size: 22px; color: #171717; }
.kg-subtitle { font-size: 13px; color: #7a7a7a; margin-top: 2px; }

.kg-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

.kg-search {
  flex: 1;
  min-width: 240px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f4f5;
  border-radius: 12px;
  padding: 10px 14px;
}
.kg-search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #171717;
}
.kg-search input::placeholder { color: #9e9e9e; }

.kg-btn-outline,
.kg-btn-solid {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.kg-btn-outline { border: 1px solid #c32e0e; color: #c32e0e; background: #fff; }
.kg-btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }
.kg-btn-solid { border: none; color: #fff; background: linear-gradient(100deg, #c32e0e 0%, #f67760 100%); }

.kg-name { font-weight: 600; color: #171717; cursor: pointer; }
.kg-name:hover { text-decoration: underline; }
.kg-key { font-size: 12px; color: #9e9e9e; margin-top: 1px; }
.kg-desc { font-size: 13px; color: #545454; }

.kg-chip { display: inline-block; border-radius: 8px; padding: 3px 10px; font-size: 12px; font-weight: 600; }
.kg-chip-node { background: #fdeae7; color: #c32e0e; }
.kg-chip-edge { background: #f4f4f5; color: #545454; }

.kg-actions { display: flex; align-items: center; justify-content: center; gap: 4px; }
.kg-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #c32e0e;
  cursor: pointer;
}
.kg-icon-btn:hover:not(:disabled) { background: #fdeae7; }
.kg-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }

/* หัวตารางสีชมพูอ่อนตามดีไซน์ */
.kg-table :deep(thead th) {
  background: #fdeae7 !important;
  color: #171717 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}
.kg-table :deep(tbody td) { font-size: 13px; }
</style>
